import api from './api/apiClient';

/**
 * 알림 타입 정의
 */
export type NotificationType =
  | 'mention'
  | 'reply'
  | 'assignment'
  | 'status_change'
  | 'deadline'
  | 'reminder'
  | 'ai_suggestion'
  | 'customer_event';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export type CustomerEventKey =
  | 'home_styling.create'
  | 'repair_request.create'
  | 'install.request.create'
  | 'install.schedule.change_request'
  | 'install.request.cancel';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  reference_type?: string; // 'happy_call', 'production', 'sales', 'purchase', 'installation_schedule', 'home_styling'
  reference_id?: string;
  reference_url?: string;
  metadata?: {
    order_number?: string;
    customer_name?: string;
    comment_preview?: string;
    [key: string]: any;
  };
  is_read: boolean;
  /** 어드민 인박스: 처리완료 시각 (NULL = 미처리) */
  resolved_at?: string | null;
  resolved_by?: string | null;
  /** 어드민 인박스: 처리자 이름 (백엔드가 resolved_by UUID→users.name 매핑 주입, 없으면 null) */
  resolved_by_name?: string | null;
  /** 어드민 인박스: 도메인 이벤트 키 */
  event_key?: string | null;
  /** 어드민 인박스: 시각적 강조도 */
  severity?: NotificationSeverity | null;
  /**
   * resolve 응답 전용 — 처리자 명의 처리 로그(댓글/메모) 기록 결과.
   * 목록 조회 응답에는 포함되지 않으며, PATCH /:id/resolve 응답 data 에만 실린다.
   */
  effect_logged?: boolean;
  effect_failed?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * 일괄 처리완료(bulk-resolve) 응답 요약.
 * effect_logged/effect_failed 는 그룹(엔티티·이벤트) 단위 처리 로그 집계 건수.
 */
export interface BulkResolveResult {
  resolved_count: number;
  effect_logged: number;
  effect_failed: number;
}

export interface InboxCounts {
  /** 안 읽은 알림 (전 타입) */
  unread: number;
  /** 미처리 어드민 인박스 (type='customer_event' AND resolved_at IS NULL) */
  unresolved: number;
  /** event_key 별 미처리 카운트 */
  byEventKey: Record<string, number>;
  /** severity 별 미처리 카운트 */
  bySeverity: { info: number; warning: number; critical: number };
}

/**
 * 알림 목록 조회 파라미터
 */
interface GetNotificationsParams {
  limit?: number;
  is_read?: boolean;
  type?: string;
  user_id: string; // TODO: 인증 미들웨어 구현 후 제거
}

/**
 * 알림 API 응답 타입
 */
interface NotificationsResponse {
  success: boolean;
  data: Notification[];
  error?: string;
}

/**
 * GET /notifications/unread-count 응답.
 *
 * 2026-08-05: 서버는 표준 응답 형식 `{ success, data: { count } }` 를 준다
 * (`src/routes/api/notifications.js:135` — «#56: 표준 응답 형식 { data } 사용»).
 * 이 타입이 `count` 를 최상위로 선언하고 있어 호출부가 `response.count` 를 읽었고,
 * 그 값이 항상 undefined 라 **안 읽은 개수가 언제나 0** 이었다 (DEV 실측 서버 283 vs 화면 0).
 */
interface UnreadCountResponse {
  success: boolean;
  data: { count: number };
  error?: string;
}

/**
 * 알림 서비스 에러
 */
export class NotificationServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: any
  ) {
    super(message);
    this.name = 'NotificationServiceError';
  }
}

/**
 * 알림 서비스
 */
class NotificationService {
  private baseUrl = '/notifications';

  /**
   * 에러 처리 유틸리티
   */
  private handleError(error: any, context: string): never {
    // 2026-08-05: 종전 로그는 { message, status, data } 만 찍었다. 부트스트랩 시점에 나는 실패에서는
    //   셋 다 undefined 라 콘솔에 `[object Object]` 만 남아 **원인을 특정할 수 없었다**
    //   (401·429·304·라우트전환취소·동시요청폭주·로드후재현을 모두 배제했으나 트리거 미확정).
    //   그래서 진단에 필요한 축을 추가한다 — 요청 취소(code:'ERR_CANCELED'/name:'CanceledError')인지,
    //   어느 URL 인지, 응답이 아예 없었는지를 구분할 수 있어야 한다.
    //   ⚠️ 레벨은 error 로 유지한다. 원인을 모르는 상태에서 낮추면 진짜 실패를 덮는다.
    //   ⚠️ 객체로만 찍으면 콘솔·수집 도구에서 `[object Object]` 로 뭉개져 아무 진단이 안 된다
    //      (이 사례에서 실제로 그랬다) → 핵심 축을 **한 줄 문자열**로 먼저 남기고,
    //      상호작용 디버깅용 원본 객체는 두 번째 인자로 덧붙인다.
    //   2026-08-05: apiClient 가 `status`·`url`·`code` 를 에러에 보존하게 되어(ApiRequestError)
    //   더 이상 axios 원본을 파헤칠 필요가 없다 — 표준 필드만 읽는다.
    const summary = [
      `status=${error?.status ?? error?.statusCode ?? error?.response?.status ?? '-'}`,
      `code=${error?.code ?? '-'}`,
      `url=${error?.url ?? error?.config?.url ?? '-'}`,
      `network=${Boolean(error?.isNetworkError)}`,
      `message=${error?.message ?? '-'}`,
    ].join(' ');
    console.error(`[NotificationService] ${context}: ${summary}`, {
      data: error?.responseData ?? error?.response?.data,
      error,
    });

    // 에러 타입별 메시지 생성
    let errorMessage = `${context} 실패`;
    let statusCode = error?.response?.status;

    if (error?.response) {
      // HTTP 에러
      switch (error.response.status) {
        case 404:
          errorMessage = '요청한 리소스를 찾을 수 없습니다.';
          break;
        case 401:
          errorMessage = '인증이 필요합니다.';
          break;
        case 403:
          errorMessage = '접근 권한이 없습니다.';
          break;
        case 500:
          errorMessage = '서버 오류가 발생했습니다.';
          break;
        default:
          errorMessage = error.response.data?.error || errorMessage;
      }
    } else if (error?.request) {
      // 네트워크 에러
      errorMessage = '네트워크 연결을 확인해주세요.';
      statusCode = 0;
    } else {
      // 기타 에러
      errorMessage = error?.message || errorMessage;
    }

    throw new NotificationServiceError(errorMessage, statusCode, error);
  }

  /**
   * 알림 목록 조회
   */
  async getNotifications(params: GetNotificationsParams): Promise<Notification[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', params.user_id);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
      if (params.type) queryParams.append('type', params.type);

      const response = await api.get<NotificationsResponse>(
        `${this.baseUrl}?${queryParams.toString()}`
      );

      return response.data || [];
    } catch (error) {
      this.handleError(error, '알림 조회');
    }
  }

  /**
   * 읽지 않은 알림 개수 조회
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const response = await api.get<UnreadCountResponse>(
        `${this.baseUrl}/unread-count?user_id=${userId}`
      );

      // 서버 형식은 { success, data: { count } } — 최상위 count 는 존재하지 않는다 (2026-08-05 정정)
      return response.data?.count || 0;
    } catch (error) {
      this.handleError(error, '읽지 않은 알림 개수 조회');
    }
  }

  /**
   * 특정 알림 조회
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async getNotification(id: string): Promise<Notification | null> {
    try {
      const response = await api.get<{ success: boolean; data: Notification }>(
        `${this.baseUrl}/${id}`
      );

      return response.data || null;
    } catch (error: any) {
      // 404는 정상 응답으로 처리 (알림 없음)
      if (error?.response?.status === 404) {
        return null;
      }
      this.handleError(error, '알림 조회');
    }
  }

  /**
   * 알림 읽음 표시
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async markAsRead(id: string, userId: string): Promise<boolean> {
    try {
      await api.put<{ success: boolean }>(
        `${this.baseUrl}/${id}/read?user_id=${userId}`,
        {}
      );

      return true;
    } catch (error) {
      this.handleError(error, '알림 읽음 표시');
    }
  }

  /**
   * 모든 알림 읽음 표시
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async markAllAsRead(userId: string): Promise<boolean> {
    try {
      await api.put<{ success: boolean; count: number }>(
        `${this.baseUrl}/read-all?user_id=${userId}`,
        {}
      );

      return true;
    } catch (error) {
      this.handleError(error, '모든 알림 읽음 표시');
    }
  }

  /**
   * 알림 삭제
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async deleteNotification(id: string, userId: string): Promise<boolean> {
    try {
      await api.delete<{ success: boolean }>(
        `${this.baseUrl}/${id}?user_id=${userId}`
      );

      return true;
    } catch (error) {
      this.handleError(error, '알림 삭제');
    }
  }

  /**
   * 읽은 알림 모두 삭제
   * @throws {NotificationServiceError} API 호출 실패 시
   */
  async deleteReadNotifications(userId: string): Promise<boolean> {
    try {
      await api.delete<{ success: boolean }>(
        `${this.baseUrl}?user_id=${userId}`
      );

      return true;
    } catch (error) {
      this.handleError(error, '읽은 알림 삭제');
    }
  }

  // ────────────────────────────────────────────────────────────
  // 어드민 인박스: 처리완료(resolve) / 카운트 API
  // ────────────────────────────────────────────────────────────

  /**
   * 알림 처리완료(resolve) 토글
   * @param id  알림 ID
   * @param opts.unresolve  true 면 처리완료를 되돌림 (resolved_at=NULL)
   */
  async resolveNotification(id: string, opts: { unresolve?: boolean } = {}): Promise<Notification | null> {
    try {
      const res = await api.patch<{ success: boolean; data: Notification }>(
        `${this.baseUrl}/${id}/resolve`,
        { unresolve: Boolean(opts.unresolve) },
      );
      return res.data || null;
    } catch (error) {
      this.handleError(error, '알림 처리완료 처리');
    }
  }

  /**
   * 다중 알림 처리완료 일괄 적용 (최대 100건)
   * @returns 처리 건수 + 처리 로그(effect) 집계 요약
   */
  async bulkResolveNotifications(ids: string[]): Promise<BulkResolveResult> {
    try {
      if (!Array.isArray(ids) || ids.length === 0) {
        return { resolved_count: 0, effect_logged: 0, effect_failed: 0 };
      }
      const res = await api.post<{
        success: boolean;
        data: { resolved_count: number; ids: string[]; effect_logged?: number; effect_failed?: number };
      }>(
        `${this.baseUrl}/bulk-resolve`,
        { ids },
      );
      return {
        resolved_count: res.data?.resolved_count || 0,
        effect_logged: res.data?.effect_logged || 0,
        effect_failed: res.data?.effect_failed || 0,
      };
    } catch (error) {
      this.handleError(error, '알림 일괄 처리완료');
    }
  }

  /**
   * 어드민 인박스 카운트 (안읽음/미처리/이벤트키별/severity별)
   */
  async getInboxCounts(): Promise<InboxCounts> {
    try {
      const res = await api.get<{ success: boolean; data: InboxCounts }>(`${this.baseUrl}/counts`);
      return res.data || { unread: 0, unresolved: 0, byEventKey: {}, bySeverity: { info: 0, warning: 0, critical: 0 } };
    } catch (error) {
      this.handleError(error, '알림 카운트 조회');
    }
  }
}

export default new NotificationService();

// ────────────────────────────────────────────────────────────
// 공유 알림 SSE 스트림 — 탭당 EventSource 1개 (ref-count 공유)
//
// [배경] useAdminInboxCounts / useBadgeCounts 가 각자 `new EventSource` 를 열어
//   한 탭에서 SSE 가 2~3개씩 생성됐다 (전역 사이드바 2개 + 랜딩 대시보드 1개).
//   프로덕션이 HTTP/1.1(nginx 1.18)로 서빙되어 origin당 동시 연결 한도가 6개인데,
//   어드민 창 2개 = SSE 3×2 = 6개로 한도를 소진 → 다른 데이터 요청이 무한 대기.
// [해결] 모든 구독자가 단 하나의 EventSource 를 공유한다. 첫 구독자에서 열고,
//   마지막 구독자가 해제되면 닫는다. 토큰이 갱신된 뒤 구독되면 재연결한다.
// ────────────────────────────────────────────────────────────

type NotificationStreamListener = (payload: unknown) => void;

let _streamSource: EventSource | null = null;
let _streamRefCount = 0;
let _streamToken: string | null = null;
const _streamListeners = new Set<NotificationStreamListener>();

function _readAuthToken(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  } catch {
    return null;
  }
}

function _openNotificationStream(): void {
  if (_streamSource) return;
  if (typeof EventSource === 'undefined') return; // SSR/미지원 환경
  const token = _readAuthToken();
  if (!token) return; // 토큰 없으면 폴링 fallback 에 위임 (401 회피)

  _streamToken = token;
  const es = new EventSource(`/api/notifications/stream?token=${encodeURIComponent(token)}`);
  es.addEventListener('notification', (e) => {
    let payload: unknown = null;
    try {
      const raw = (e as MessageEvent).data;
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      /* 비 JSON payload 무시 */
    }
    _streamListeners.forEach((fn) => {
      try {
        fn(payload);
      } catch {
        /* 개별 구독자 에러 격리 */
      }
    });
  });
  es.onerror = () => {
    /* EventSource 내장 자동 재연결 — 폴링 fallback 이 공백을 보완 */
  };
  _streamSource = es;
}

function _closeNotificationStream(): void {
  if (_streamSource) {
    try {
      _streamSource.close();
    } catch {
      /* noop */
    }
  }
  _streamSource = null;
  _streamToken = null;
}

/**
 * 공유 알림 SSE 구독. 탭당 단 하나의 EventSource 를 ref-count 로 공유한다.
 *
 * @param listener  `notification` 이벤트 수신 콜백 (JSON 파싱된 payload, 비 JSON 이면 null)
 * @returns 구독 해제 함수 (마지막 구독자 해제 시 연결 close)
 */
export function subscribeNotificationStream(listener: NotificationStreamListener): () => void {
  _streamListeners.add(listener);
  _streamRefCount += 1;

  // 로그인 토큰이 갱신된 상태에서 새로 구독되면 이전 연결을 끊고 새 토큰으로 재연결
  const token = _readAuthToken();
  if (_streamSource && token && token !== _streamToken) {
    _closeNotificationStream();
  }
  _openNotificationStream();

  let active = true;
  return () => {
    if (!active) return; // 중복 해제 방어
    active = false;
    _streamListeners.delete(listener);
    _streamRefCount = Math.max(0, _streamRefCount - 1);
    if (_streamRefCount === 0) {
      _closeNotificationStream();
    }
  };
}

/** 테스트/디버그용 — 현재 공유 스트림 상태 스냅샷 */
export function _getNotificationStreamState(): { open: boolean; refCount: number; listeners: number } {
  return { open: _streamSource !== null, refCount: _streamRefCount, listeners: _streamListeners.size };
}
